import { useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Lock } from "lucide-react";

import { Button } from "./Button";
import { Card } from "./Card";
import { useAuth } from "./contexts/AuthContext";
import { PLANS_ON_SALE, hasAgencyFeature } from "@/utils/agencyFeatures";
import { PLAN_PATH } from "../Pages/Settings/settingsPaths";

/**
 * Aviso de funcionalidad no incluida en el plan. Solo el admin de agencia contrata (Ajustes → Plan),
 * así que solo él ve «Ver planes».
 */
export const PlanFeatureLocked = ({ title }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setPageContext } = useOutletContext();
  const primary = theme.palette.primary.main;
  const isAdmin = user.role === "agency_admin";

  useEffect(() => {
    setPageContext?.({ title, showTitle: true });
  }, [setPageContext, title]);

  return (
    <Card sx={{ alignItems: "center", textAlign: "center", gap: 2, px: 3, py: 6, maxWidth: 520, mx: "auto", mt: 4 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: alpha(primary, 0.1),
          color: primary,
        }}
      >
        <Lock size={22} />
      </Box>
      <Typography sx={{ fontWeight: 600, fontSize: "1.0625rem" }}>
        Tu plan no te permite acceder a esta funcionalidad
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, maxWidth: 400 }}>
        {!PLANS_ON_SALE
          ? "Esta funcionalidad no está incluida en el plan de tu agencia."
          : isAdmin
            ? "Mejora tu plan para desbloquearla."
            : "Pide al administrador de tu agencia que mejore el plan para desbloquearla."}
      </Typography>
      {PLANS_ON_SALE && isAdmin && <Button onClick={() => navigate(PLAN_PATH)}>Ver planes</Button>}
    </Card>
  );
};

PlanFeatureLocked.propTypes = { title: PropTypes.string.isRequired };

/** Pinta la página si el plan de la agencia incluye `feature`; si no, el aviso para contratar. */
export const PlanFeatureGate = ({ feature, title, children }) => {
  const { user } = useAuth();
  return hasAgencyFeature(user, feature) ? children : <PlanFeatureLocked title={title} />;
};

PlanFeatureGate.propTypes = {
  feature: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};
